/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';
import { type ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { getAtlassianStudioNLCreateUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';
import { useUserSitePermissions } from '@atlassian/conversation-assistant-agent/controllers/agent-site-permissions';
import {
	useAgentsPaginatedActionsWithOptimistic,
	useRecommendedAgents,
} from '@atlassian/conversation-assistant-agent/controllers/agents';
import { getDisplayedAgents } from '@atlassian/conversation-assistant-agent/utils/getDisplayedAgents';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	type Agent,
	type ApiTaskSSEUpdate,
	isChannelNotFoundError,
	type PinnedConversationSummaryResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	ApiError,
	getPinnedConversationLimitErrorData,
} from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { type Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import {
	unifiedTaskToApiSSE,
	useActiveTasksForConversations,
} from '@atlassian/conversation-assistant-store/controllers/unified-tasks';
import { useUnifiedTasksSSESubscription } from '@atlassian/conversation-assistant-store/controllers/unified-tasks/use-unified-tasks-sse-subscription';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useConversationModal } from '@atlassian/conversation-assistant-ui-components/hooks/useConversationModal';
import { getAgentSessionStateBadgeStatus } from '@atlassian/conversation-assistant-ui-components/utils/conversation-task-status-indicator';
import { useAgentCrossProductUrlWrapper } from '@atlassian/rovo-agent-url-utils/use-agent-cross-product-url';
import { useSpaceStoreActions } from '@atlassian/rovo-spaces/controllers/space-store';
import { addConversationToSpaceFetchAction } from '@atlassian/rovo-spaces/services/useAddConversationToSpace/fetch';

import { mapConversationToConversationUIComponent } from '../../common/utils/conversation-assistant-ui-component';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useSideNav } from '../../common/utils/side-nav-context/useSideNav';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useWindowLocation } from '../../common/utils/use-window-location/useWindowLocation';
import { useExitSpace } from '../../hooks/use-exit-space';
import { useActiveAction } from '../active-action';
import { isEmptyConversation } from '../chat-store/isEmptyConversation';
import { type MessageSource } from '../chat-store/types';
import { useChatStore } from '../chat-store/useChatStore';
import { useChatStoreActions } from '../chat-store/useChatStoreActions';
import { useConversation } from '../conversation/useConversation';
import { useConversationActions } from '../conversation/useConversationActions';
import {
	getPersistedRecentConversationId,
	persistRecentConversation,
} from '../recent-conversation-persistence/recentConversationPersistence';
import { performAgentSelection } from './perform-agent-selection';

type WithLiveTask<T> = T & { currentTask?: ApiTaskSSEUpdate | null };
type ConversationWithLiveTask = WithLiveTask<Conversation> & { isEmpty: boolean };

const CONVERSATION_LIMIT = 25;

const getTimestamp = (timestamp?: string | null) => {
	if (!timestamp) {
		return undefined;
	}

	const time = new Date(timestamp).getTime();

	return Number.isNaN(time) ? undefined : time;
};

const compareTimestampsDescending = (
	aTimestamp?: string | null,
	bTimestamp?: string | null,
): number => {
	const aTime = getTimestamp(aTimestamp);
	const bTime = getTimestamp(bTimestamp);

	if (aTime === undefined && bTime === undefined) {
		return 0;
	}
	if (aTime === undefined) {
		return 1;
	}
	if (bTime === undefined) {
		return -1;
	}

	return bTime - aTime;
};

const compareConversationsByPinnedBadgeThenRecent = (
	a: ConversationWithLiveTask,
	b: ConversationWithLiveTask,
) => {
	const aIsPinned = Boolean(a.isPinned);
	const bIsPinned = Boolean(b.isPinned);

	if (aIsPinned !== bIsPinned) {
		return aIsPinned ? -1 : 1;
	}

	if (aIsPinned && bIsPinned) {
		const pinnedSort = compareTimestampsDescending(a.pinnedAt, b.pinnedAt);

		if (pinnedSort !== 0) {
			return pinnedSort;
		}
	}

	if (!aIsPinned && !bIsPinned) {
		const aHasStatusBadge = Boolean(getAgentSessionStateBadgeStatus(a.agentSessionState));
		const bHasStatusBadge = Boolean(getAgentSessionStateBadgeStatus(b.agentSessionState));

		if (aHasStatusBadge !== bHasStatusBadge) {
			return aHasStatusBadge ? -1 : 1;
		}
	}

	return compareTimestampsDescending(a.lastMessageTimestamp, b.lastMessageTimestamp);
};

export type SendMessageParams = {
	name: string;
	agent: Agent;
	prompt: DocNode;
	conversationId?: string;
	storeUserMessages: boolean;
	messageSources: Partial<MessageSource>;
};

type AgentSelection = Pick<Agent, 'id' | 'is_default'>;

export type UseRovoNavigationResult = {
	siteId: string;
	conversations: Array<WithLiveTask<Conversation> & { isEmpty: boolean }>;
	pinnedCountConversations: Array<WithLiveTask<Conversation> & { isEmpty: boolean }>;
	/**
	 * @deprecated Pin limits are enforced by the pin mutation and surfaced through a structured 409.
	 * This remains optional for source compatibility and is no longer populated.
	 */
	pinnedConversationSummary?: PinnedConversationSummaryResponse;
	currentConversation: (WithLiveTask<Conversation> & { isEmpty: boolean; id: string }) | undefined;
	agents: Agent[];
	recommendedAgents: Agent[];
	selectedAgent: Agent | undefined;
	isAbleToCreateAgents: boolean;
	isFetchingRecommendedAgents: boolean;
	isConversationsInitialized?: boolean;
	handleConversationClick: (conversation: Conversation, isADSNavigationEnabled: boolean) => void;
	handleCreateAgentClick: () => void;
	handleNewChatClick: (isADSNavigationEnabled: boolean) => void;
	handleSelectAgent: (agent: AgentSelection, isADSNavigationEnabled: boolean) => void;
	handleViewAllAgentsClick: () => void;
	handleViewAllSpacesClick: () => void;
	openAllConversations: () => void;
	handleViewAllConversationsClick: () => void;
	handlePulseClick: () => void;
	handleTasksClick: () => void;
	handleLibraryClick: () => void;
	handleScheduledChatsClick: () => void;
	handleSelectRecommendedSpacesClick: () => void;
	handleConversationStarterClick: (
		agent: Agent,
		starter: ConversationStarter,
		isADSNavigationEnabled: boolean,
	) => void;
	handleConversationPinToggle: (conversation: Conversation, isPinned: boolean) => Promise<void>;
	deleteAgentLocally: (id: string) => { restore: () => void };
	deleteConversation: {
		conversationToBeDeleted: Conversation | undefined;
		openDeleteModal: (target: Conversation) => Promise<void>;
		handleDeleteConfirm: (conversationId: string) => Promise<{ error: string } | undefined>;
	};
	editConversation: {
		handleEditConversationConfirm: (
			conversation: Conversation,
			newName: string,
		) => Promise<{ error: string } | undefined>;
		openEditModal: (target: Conversation) => Promise<void>;
		conversationToBeEdited: Conversation | undefined;
	};
	moveToSpace: {
		handleMoveConversationToSpace: (conversation: Conversation, spaceId: string) => Promise<void>;
		handleMoveConversationToNewSpace: (conversation: Conversation) => void;
		conversationToMoveToNewSpace: Conversation | undefined;
		clearConversationToMoveToNewSpace: () => void;
	};
};

export type UseRovoNavigationProps = {
	// max number of conversations to show in nav bar, defaults to 25.
	conversationLimit?: number;
	// consumers of useRovoNavigation must provide their own implementation of send message
	// as they may not be within our store scope
	onConversationStarterClick?: (params: SendMessageParams) => void;
	// when true, tasks feature is enabled. Must be provided by the consumer.
	isTasksEnabled?: boolean;
	// whether the navigation panel is open/visible. Used to gate the SSE connection.
	isNavOpen?: boolean;
	/**
	 * Optional host callback fired when the user clicks the "View all spaces"
	 * link in the side nav (which becomes visible only when this is provided).
	 * The hook stays no-op-safe when undefined, so existing hosts that have not
	 * opted in are unaffected. Mirrors the existing `onViewAllAgentsClick`
	 * wiring.
	 */
	onViewAllSpacesClick?: () => void;
} & AssistanceServiceInjectedParams;

export const useRovoNavigation: (
	props: Omit<UseRovoNavigationProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseRovoNavigationResult = createHookWithAssistanceService(
	({
		conversationLimit = CONVERSATION_LIMIT,
		assistanceServiceInjectedParams,
		onConversationStarterClick,
		isTasksEnabled: isTasksEnabledProp,
		isNavOpen,
		onViewAllSpacesClick,
	}: UseRovoNavigationProps): UseRovoNavigationResult => {
		di(
			useUserSitePermissions,
			useChatStoreActions,
			useGlobalConfig,
			useRecommendedAgents,
			useChatStore,
			useChatStoreActions,
			useUIStore,
			useUIConfig,
			useAgentCrossProductUrlWrapper,
		);
		const { siteId, touchpointSource } = assistanceServiceInjectedParams;
		const wrapCreateAgentUrl = useAgentCrossProductUrlWrapper({
			xpcBridge: 'rovoChat-createAgentButton',
			gate: 'rsa-1417-ai-mate-xpc-surfaces',
		});

		const experienceTracker = useAIMateExperienceTracker();

		const [
			{ conversations, isConversationsInitialized, conversationPagination },
			// warning: do not use actions from Chat Store that require assistance service without extracting them and wrapping in createHookWithAssistanceService
			{ setSelectedConversation, loadMoreConversationChannelsData },
		] = useChatStore();

		const { setConversationData } = useChatStoreActions();
		const { removeConversationFromSpaceConversations } = useSpaceStoreActions();

		const { deleteConversation, updateConversation } = useConversationActions({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		const { sendAnalyticsEvent } = useAnalytics();

		const [rovoAgentCSID, { trackCreateSessionStart }] = useRovoAgentCreateAnalytics({
			touchPoint: touchpointSource,
		});
		const { email } = useProfileController();
		const { conversation, selectedAgent } = useConversation({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});
		const { openModal } = useConversationModal();
		const { clearActiveAction } = useActiveAction();
		const {
			preferences: { storeUserMessages },
		} = useUserPreferences();
		const { deleteAgentLocally } = useAgentsPaginatedActionsWithOptimistic({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});
		const { activeMenu, setActiveMenu } = useUIStore();
		const exitSpace = useExitSpace();
		const { setSideNavOpen } = useSideNav();
		const { isAbleToCreateAgents } = useUserSitePermissions({
			siteId,
			touchpointSource: 'rovoNavigation',
		});
		const pageUrl = useWindowLocation();
		const { recommendedAgents, isFetching: isFetchingRecommendedAgents } = useRecommendedAgents({
			assistanceServiceParams: assistanceServiceInjectedParams,
			pageUrl,
		});
		const isAgentSessionStateBadgeEnabled = fg('rovo-chat-ai-inbox');

		const [conversationToBeDeleted, setConversationToBeDeleted] = useState<
			Conversation | undefined
		>(undefined);
		const [conversationToBeEdited, setConversationToBeEdited] = useState<Conversation | undefined>(
			undefined,
		);
		const [conversationToMoveToNewSpace, setConversationToMoveToNewSpace] = useState<
			Conversation | undefined
		>(undefined);

		const sendClickEvent = useCallback(
			(actionSubjectId: string, attributes?: Record<string, unknown>, actionSubject?: string) =>
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: actionSubject || 'button',
					actionSubjectId,
					attributes: { touchpointSource, ...attributes },
				}),
			[sendAnalyticsEvent, touchpointSource],
		);
		const canLoadMoreConversations =
			conversationPagination.hasMore &&
			!conversationPagination.isLoadingMore &&
			Boolean(conversationPagination.nextCursor);

		const handleConversationClick = useCallback(
			(conversation: Conversation, isADSNavigationEnabled: boolean) => {
				sendClickEvent('chatViewChatItemClick', {
					conversationId: conversation.id,
				});

				// Exit any active space when explicitly switching to a conversation — the selected
				// conversation may not belong to any space, so previous space state (selectedSpace,
				// empty-chat launch state, and aiFeatureContext.projectContext) must not carry
				// over into the new conversation.
				exitSpace();

				setSelectedConversation({
					conversationId: conversation.id,
					conversationMetadata: conversation,
				});

				if (!isADSNavigationEnabled) {
					setSideNavOpen(false);
					clearActiveAction();
				}
				if (activeMenu !== 'none') {
					setActiveMenu('none');
				}
			},
			[
				activeMenu,
				exitSpace,
				setActiveMenu,
				clearActiveAction,
				setSelectedConversation,
				setSideNavOpen,
				sendClickEvent,
			],
		);

		const handleSelectAgent = useCallback(
			(agent: AgentSelection, isADSNavigationEnabled: boolean) => {
				performAgentSelection({
					agent,
					activeMenu,
					touchpointSource,
					isADSNavigationEnabled,
					sendAnalyticsEvent,
					setActiveMenu,
					exitSpace,
					setSelectedConversation,
					setSideNavOpen,
				});
			},
			[
				activeMenu,
				exitSpace,
				sendAnalyticsEvent,
				setActiveMenu,
				setSelectedConversation,
				setSideNavOpen,
				touchpointSource,
			],
		);

		const handleEditConversationConfirm = useCallback(
			async (conversation: Conversation, newName: string) => {
				sendClickEvent('rovoSideNavEditConversationConfirmButton');
				try {
					await updateConversation({
						conversationId: conversation.id,
						data: {
							name: newName.trim(),
						},
					});
				} catch (error) {
					if (error instanceof ApiError && isChannelNotFoundError(error)) {
						return {
							error: error.message,
						};
					}

					throw error;
				}
			},
			[updateConversation, sendClickEvent],
		);

		const handleConversationPinToggle = useCallback(
			async (conversation: Conversation, isPinned: boolean) => {
				sendClickEvent(isPinned ? 'pinConversationButton' : 'unpinConversationButton');
				try {
					await updateConversation({
						conversationId: conversation.id,
						data: { isPinned },
					});
				} catch (error) {
					const pinLimitErrorData = isPinned
						? getPinnedConversationLimitErrorData(error)
						: undefined;
					if (pinLimitErrorData !== undefined) {
						sendAnalyticsEvent({
							action: 'rejected',
							actionSubject: 'operation',
							actionSubjectId: 'pinConversation',
							attributes: {
								reason: 'pinned_conversation_limit_reached',
								touchpointSource,
								...pinLimitErrorData,
							},
						});
					}
					throw error;
				}
			},
			[sendAnalyticsEvent, sendClickEvent, touchpointSource, updateConversation],
		);

		const openDeleteModal = useCallback(
			async (target: Conversation) => {
				sendClickEvent('deleteConversationButton');
				setConversationToBeDeleted(target);
				openModal(mapConversationToConversationUIComponent(target), 'delete');
			},
			[setConversationToBeDeleted, openModal, sendClickEvent],
		);

		const openEditModal = useCallback(
			async (target: Conversation) => {
				sendClickEvent('rovoSideNavEditConversation');
				setConversationToBeEdited(target);
				openModal(mapConversationToConversationUIComponent(target), 'edit');
			},
			[setConversationToBeEdited, openModal, sendClickEvent],
		);

		const handleNewChatClick = useCallback(
			(isADSNavigationEnabled: boolean): void => {
				sendClickEvent('chatViewNewChatButton');

				// Exit any active space when starting a new chat — a fresh conversation has no
				// space context, so previous space state must not carry over.
				exitSpace();

				setSelectedConversation({
					conversationId: EMPTY_CONVERSATION_ID,
				});

				if (!isADSNavigationEnabled) {
					setSideNavOpen(false);
					clearActiveAction();
				}
				if (activeMenu !== 'none') {
					setActiveMenu('none');
				}
			},
			[
				activeMenu,
				exitSpace,
				setActiveMenu,
				clearActiveAction,
				setSelectedConversation,
				setSideNavOpen,
				sendClickEvent,
			],
		);

		const handleDeleteConfirm = useCallback(
			async (conversationId: string) => {
				try {
					await deleteConversation({
						conversationId,
					});
					const backendConversationId = conversationToBeDeleted?.backendConversationId;
					removeConversationFromSpaceConversations(
						backendConversationId ? [conversationId, backendConversationId] : [conversationId],
					);
				} catch (error) {
					if (error instanceof ApiError && isChannelNotFoundError(error)) {
						return {
							error: error.message,
						};
					}

					throw error;
				} finally {
					setConversationToBeDeleted(undefined);
				}
			},
			[conversationToBeDeleted, deleteConversation, removeConversationFromSpaceConversations],
		);

		const handleCreateAgentClick = useCallback(() => {
			sendClickEvent(
				'browseAgentsCreateAgentButton',
				{ interactionSource: touchpointSource },
				'link',
			);

			trackCreateSessionStart();
			window.open(
				wrapCreateAgentUrl(
					getAtlassianStudioNLCreateUrl(siteId, touchpointSource, rovoAgentCSID, email),
				),
				'_blank',
				'noopener, noreferrer',
			);
		}, [
			siteId,
			sendClickEvent,
			touchpointSource,
			trackCreateSessionStart,
			rovoAgentCSID,
			email,
			wrapCreateAgentUrl,
		]);

		const handleViewAllAgentsClick = useCallback(() => {
			sendClickEvent('browseAgentsNavigate', { interactionSource: touchpointSource });

			if (activeMenu !== 'agents') {
				setActiveMenu('agents');
			}
			setSideNavOpen(false);
		}, [sendClickEvent, setSideNavOpen, activeMenu, setActiveMenu, touchpointSource]);

		const handleViewAllSpacesClick = useCallback(() => {
			// Always emit the analytics click event so we can measure intent even
			// before the destination route exists in every host. Mirrors
			// `handleViewAllAgentsClick`.
			sendClickEvent('browseSpacesNavigate', { interactionSource: touchpointSource });

			// Allow hosts to override / augment the default behaviour by passing
			// a callback (e.g. Townsquare will navigate to its standalone route).
			// When the host provides one, we DEFER to it entirely and skip
			// flipping `activeMenu` — otherwise we'd leave stale store state
			// pointing at `'browse-spaces'` after the host routes away.
			if (onViewAllSpacesClick) {
				onViewAllSpacesClick();
			} else {
				// In-app default: flip the activeMenu to `'browse-spaces'` so
				// `conversation-assistant`'s `main.tsx` renders the
				// `BrowseSpacesView` panel (mirroring how `handleViewAllAgentsClick`
				// flips to `'agents'` to show `BrowseAgentsView`). This makes the
				// link work out-of-the-box for every host that mounts
				// `conversation-assistant` (e.g. Confluence) without needing a
				// host-level route. FE-B5b will register a real Townsquare route +
				// FE-B5c will wire Confluence/Jira embed flows; until then this
				// in-panel view is the canonical destination.
				if (activeMenu !== 'browse-spaces') {
					setActiveMenu('browse-spaces');
				}
			}

			setSideNavOpen(false);
		}, [
			sendClickEvent,
			setSideNavOpen,
			touchpointSource,
			onViewAllSpacesClick,
			activeMenu,
			setActiveMenu,
		]);

		const openAllConversations = useCallback(() => {
			if (activeMenu !== 'conversation-list') {
				setActiveMenu('conversation-list');
			}

			if (canLoadMoreConversations && !fg('rovo_chat_all_chat_agent_sessions')) {
				void loadMoreConversationChannelsData();
			}

			setSideNavOpen(false);
		}, [
			activeMenu,
			canLoadMoreConversations,
			loadMoreConversationChannelsData,
			setActiveMenu,
			setSideNavOpen,
		]);

		const handleViewAllConversationsClick = useCallback(() => {
			sendClickEvent('rovoSideNavViewAllConversations');
			openAllConversations();
		}, [openAllConversations, sendClickEvent]);

		const handlePulseClick = useCallback(() => {
			if (activeMenu !== 'pulse') {
				setActiveMenu('pulse');
			}
			setSideNavOpen(false);
		}, [activeMenu, setActiveMenu, setSideNavOpen]);

		const handleTasksClick = useCallback(() => {
			if (activeMenu !== 'tasks') {
				setActiveMenu('tasks');
			}
			setSideNavOpen(false);
		}, [activeMenu, setActiveMenu, setSideNavOpen]);

		const handleLibraryClick = useCallback(() => {
			if (activeMenu !== 'library') {
				setActiveMenu('library');
			}
			setSideNavOpen(false);
		}, [activeMenu, setActiveMenu, setSideNavOpen]);

		const handleScheduledChatsClick = useCallback(() => {
			if (activeMenu !== 'scheduled') {
				setActiveMenu('scheduled');
			}
			setSideNavOpen(false);
		}, [activeMenu, setActiveMenu, setSideNavOpen]);

		const handleSelectRecommendedSpacesClick = useCallback(() => {
			if (activeMenu !== 'recommended-spaces') {
				// Start the perceived-load experience at click. It ends when the
				// RecommendedSpacesPanel finishes loading — capturing click→render
				// (which the ROVOFE-3252 prefetch shortens when the cache is warm).
				experienceTracker.start({
					id: ExperienceName.OPEN_RECOMMENDED_SPACES,
					name: ExperienceName.OPEN_RECOMMENDED_SPACES,
				});
				setActiveMenu('recommended-spaces');
			}
			setSideNavOpen(false);
		}, [activeMenu, setActiveMenu, setSideNavOpen, experienceTracker]);

		const handleConversationStarterClick = useCallback(
			(agent: Agent, starter: ConversationStarter, isADSNavigationEnabled: boolean) => {
				sendClickEvent('rovoSideNavProfileCardStarter', {
					agentId: agent.id,
				});

				if (!isADSNavigationEnabled) {
					setSideNavOpen(false);
				}
				onConversationStarterClick?.({
					name: starter.message,
					prompt: encodeAdf(starter.message),
					agent,
					// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
					// value when BE user settings are fully rolled out; BE overrides it via the capability header.
					storeUserMessages,
					messageSources: {
						interactionSource: 'profileCardConversationStarters',
						followUpSource: 'none',
					},
				});
			},
			[storeUserMessages, setSideNavOpen, sendClickEvent, onConversationStarterClick],
		);

		// Initialise SSE connection for live task updates when tasks are enabled and nav is open.
		useUnifiedTasksSSESubscription({
			enabled: !!isTasksEnabledProp && isNavOpen !== false,
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		// Single batched hook for active tasks across all conversations + the current one.
		// Sweet-state re-runs the selector on every state change but the resulting Map is
		// only structurally re-derived when an active task in this set actually changes.
		const visibleConvIds = useMemo(() => {
			const ids = Object.keys(conversations);
			if (conversation && !ids.includes(conversation.id)) {
				ids.push(conversation.id);
			}
			return ids;
		}, [conversations, conversation]);
		const [activeTasksByConvId] = useActiveTasksForConversations(visibleConvIds);

		const transformedConversationsForPinnedCount = useMemo(
			() =>
				Object.values(conversations)
					.map((conversation) => {
						const activeTask = activeTasksByConvId.get(conversation.id);
						return {
							...conversation,
							currentTask: activeTask ? unifiedTaskToApiSSE(activeTask) : null,
							isEmpty: isEmptyConversation(conversation.id),
						};
					})
					.sort((a, b) =>
						isAgentSessionStateBadgeEnabled
							? compareConversationsByPinnedBadgeThenRecent(a, b)
							: compareTimestampsDescending(a.lastMessageTimestamp, b.lastMessageTimestamp),
					),
			[conversations, activeTasksByConvId, isAgentSessionStateBadgeEnabled],
		);

		const transformedConversations = useMemo(
			() => transformedConversationsForPinnedCount.slice(0, conversationLimit),
			[conversationLimit, transformedConversationsForPinnedCount],
		);

		const currentConversation = useMemo(() => {
			if (!conversation) {
				return undefined;
			}
			const activeTask = activeTasksByConvId.get(conversation.id);
			return {
				...conversation,
				currentTask: activeTask ? unifiedTaskToApiSSE(activeTask) : null,
				isEmpty: isEmptyConversation(conversation.id),
				id: conversation.id,
			};
		}, [conversation, activeTasksByConvId]);

		const agents = useMemo(
			() =>
				getDisplayedAgents({
					recommendedAgents,
					agentsByCategoryAllAgents: [],
					agentsByCategoryFavoriteAgents: [],
					selectedAgent,
					moveSelectedAgentToTop: false,
				}),
			[recommendedAgents, selectedAgent],
		);

		const handleMoveConversationToSpace = useCallback(
			async (conversation: Conversation, spaceId: string) => {
				sendClickEvent('rovoSideNavMoveConversationToSpace', { spaceId });

				// Optimistically move the conversation into the target space so the
				// UI updates immediately without requiring a page refresh.
				const previousSpaceId = conversation.rovoSpaceId;
				setConversationData({
					conversationId: conversation.id,
					data: { rovoSpaceId: spaceId },
				});

				try {
					await addConversationToSpaceFetchAction({
						request: { spaceId, conversationId: conversation.id },
						cloudId: siteId,
						product: assistanceServiceInjectedParams.product ?? 'rovo',
						experienceId: assistanceServiceInjectedParams.experienceId ?? 'ai-mate',
					});
					const storedConversationId = getPersistedRecentConversationId(siteId);
					if (
						storedConversationId === conversation.id ||
						storedConversationId === conversation.backendConversationId
					) {
						persistRecentConversation({
							type: 'conversation-selected',
							siteId,
							conversationId: EMPTY_CONVERSATION_ID,
						});
					}
				} catch (error) {
					// Roll back the optimistic update if the API call fails.
					setConversationData({
						conversationId: conversation.id,
						data: { rovoSpaceId: previousSpaceId },
					});
					throw error;
				}
			},
			[sendClickEvent, siteId, assistanceServiceInjectedParams, setConversationData],
		);

		const handleMoveConversationToNewSpace = useCallback(
			(conversation: Conversation) => {
				sendClickEvent('rovoSideNavMoveConversationToNewSpace');
				setConversationToMoveToNewSpace(conversation);
			},
			[sendClickEvent],
		);

		const clearConversationToMoveToNewSpace = useCallback(() => {
			setConversationToMoveToNewSpace(undefined);
		}, []);

		return {
			siteId,
			conversations: transformedConversations,
			pinnedCountConversations: transformedConversationsForPinnedCount,
			pinnedConversationSummary: undefined,
			currentConversation,
			agents,
			recommendedAgents,
			selectedAgent,
			isAbleToCreateAgents,
			isFetchingRecommendedAgents,
			isConversationsInitialized,
			handleConversationClick,
			handleCreateAgentClick,
			handleNewChatClick,
			handleSelectAgent,
			handleViewAllAgentsClick,
			handleViewAllSpacesClick,
			openAllConversations,
			handleViewAllConversationsClick,
			handlePulseClick,
			handleTasksClick,
			handleLibraryClick,
			handleScheduledChatsClick,
			handleSelectRecommendedSpacesClick,
			handleConversationStarterClick,
			handleConversationPinToggle,
			deleteAgentLocally,
			deleteConversation: {
				conversationToBeDeleted,
				openDeleteModal,
				handleDeleteConfirm,
			},
			editConversation: {
				handleEditConversationConfirm,
				openEditModal,
				conversationToBeEdited,
			},
			moveToSpace: {
				handleMoveConversationToSpace,
				handleMoveConversationToNewSpace,
				conversationToMoveToNewSpace,
				clearConversationToMoveToNewSpace,
			},
		};
	},
);
